import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import AnalyticsService from '../../services/analyticsService';
import WorkoutService from '../../services/workoutService';
import ReminderService from '../../services/reminderService';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Paper,
  Stack,
  Link,
  Avatar,
  Chip
} from '@mui/material';
import {
  FitnessCenter as WorkoutIcon,
  List as ExerciseIcon,
  ShowChart as ProgressIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  DirectionsRun as RunningIcon,
  Repeat as RepeatIcon,
  Whatshot as FireIcon
} from '@mui/icons-material';

// Import our custom components
import PageHeader from '../layout/PageHeader';
import DataCard from '../common/DataCard';
import EmptyState from '../common/EmptyState';
import Loading from '../layout/Loading';

const Dashboard = () => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalWorkouts: 0,
    totalExercises: 0,
    totalSets: 0,
    totalVolume: 0
  });
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch workout summary
        const summaryData = await AnalyticsService.getSummary('monthly', token);

        if (summaryData.success) {
          setStats({
            totalWorkouts: summaryData.stats.totalWorkouts || 0,
            totalExercises: summaryData.stats.totalExercises || 0,
            totalSets: summaryData.stats.totalSets || 0,
            totalVolume: summaryData.stats.totalVolume || 0
          });
        }

        // Fetch recent workouts
        const workoutsData = await WorkoutService.getAllWorkouts(token, { limit: 5 });

        if (workoutsData.success) {
          setRecentWorkouts(workoutsData.workouts || []);
        }

        // Fetch upcoming reminders
        const remindersData = await ReminderService.getUpcomingReminders(token);

        if (remindersData.success) {
          setUpcomingReminders(remindersData.reminders || []);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return <Loading message="Loading dashboard..." />;
  }

  return (
    <Box>
      <PageHeader
        title={`Welcome, ${user.name}!`}
        subtitle="Track your fitness journey and monitor your progress"
      />
      
      {/* Quick Actions */}
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ p: 2, mb: 4, borderRadius: 2 }}
      >
        <Typography variant="h6" gutterBottom sx={{ pl: 1 }}>
          Quick Actions
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2}
          sx={{ p: 1 }}
        >
          <Button 
            component={RouterLink} 
            to="/workout" 
            variant="contained" 
            startIcon={<WorkoutIcon />}
            fullWidth
          >
            Start Workout
          </Button>
          <Button 
            component={RouterLink} 
            to="/exercises" 
            variant="outlined"
            startIcon={<ExerciseIcon />}
            fullWidth
          >
            Browse Exercises
          </Button>
          <Button 
            component={RouterLink} 
            to="/progress" 
            variant="outlined"
            startIcon={<ProgressIcon />}
            fullWidth
          >
            View Progress
          </Button>
        </Stack>
      </Paper>
      
      {/* Stats Overview */}
      <Typography variant="h6" gutterBottom>
        Monthly Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Workouts"
            value={stats.totalWorkouts}
            icon={<WorkoutIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Exercises"
            value={stats.totalExercises}
            icon={<ExerciseIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Sets"
            value={stats.totalSets}
            icon={<RepeatIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Volume"
            value={stats.totalVolume.toLocaleString()}
            unit="kg"
            icon={<FireIcon />}
            color="warning"
          />
        </Grid>
      </Grid>
      
      {/* Recent Workouts */}
      <Typography variant="h6" gutterBottom>
        Recent Workouts
      </Typography>
      {recentWorkouts.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {recentWorkouts.map((workout) => (
            <Grid item xs={12} sm={6} md={4} key={workout._id}>
              <Card elevation={0} variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" component="h3">
                      {workout.title}
                    </Typography>
                    <Chip 
                      icon={<CalendarIcon fontSize="small" />} 
                      label={formatDate(workout.date)} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RunningIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {workout.exercises.length} exercises
                    </Typography>
                  </Box>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button 
                    component={RouterLink} 
                    to={`/history/${workout._id}`} 
                    size="small" 
                    color="primary"
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ mb: 4 }}>
          <EmptyState
            title="No recent workouts"
            description="Start tracking your first workout to see your progress!"
            icon={<WorkoutIcon />}
            actionText="Start Workout"
            onAction={() => window.location.href = '/workout'}
          />
        </Box>
      )}
      
      {/* Upcoming Reminders */}
      <Typography variant="h6" gutterBottom>
        Upcoming Reminders
      </Typography>
      {upcomingReminders.length > 0 ? (
        <Grid container spacing={3}>
          {upcomingReminders.map((reminder) => (
            <Grid item xs={12} sm={6} md={4} key={reminder._id}>
              <Card elevation={0} variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <EventIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" component="h3">
                        {reminder.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(reminder.scheduledDate)}
                        </Typography>
                      </Box>
                      {reminder.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {reminder.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          title="No upcoming reminders"
          description="Set reminders to stay on track with your workout schedule"
          icon={<EventIcon />}
          actionText="Set Reminder"
          onAction={() => window.location.href = '/reminders'}
        />
      )}
    </Box>
  );
};

export default Dashboard;
